import {useEffect, useRef, useState} from 'react';

interface UseScrollCarouselOptions {
  totalItems: number;
  scrollThreshold?: number;
  enabled?: boolean;
}

interface UseScrollCarouselReturn {
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  carouselRef: React.RefObject<HTMLDivElement>;
}

export const useScrollCarousel = ({
  totalItems,
  scrollThreshold = 0.3,
  enabled = true,
}: UseScrollCarouselOptions): UseScrollCarouselReturn => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const lastScrollTime = useRef<number>(0);
  const scrollTimeout = useRef<NodeJS.Timeout>();
  const manualNavigationRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled || totalItems <= 1) return;

    const handleScroll = () => {
      const now = Date.now();

      // Throttle scroll events to prevent excessive updates
      if (now - lastScrollTime.current < 100) return;
      lastScrollTime.current = now;

      // Clear existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      // Set a new timeout to update the carousel after scrolling stops
      scrollTimeout.current = setTimeout(() => {
        if (!carouselRef.current) return;

        const rect = carouselRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Calculate how much of the carousel is visible
        const visibleTop = Math.max(0, -rect.top);
        const visibleBottom = Math.min(rect.height, windowHeight - rect.top);
        const visibleHeight = visibleBottom - visibleTop;
        const visibilityRatio = visibleHeight / rect.height;

        // Only advance if the carousel is sufficiently visible and not manually navigated
        if (
          visibilityRatio >= scrollThreshold &&
          !manualNavigationRef.current
        ) {
          const scrollProgress = Math.max(
            0,
            Math.min(1, -rect.top / windowHeight)
          );
          const newIndex = Math.floor(scrollProgress * totalItems);
          const clampedIndex = Math.max(0, Math.min(totalItems - 1, newIndex));

          if (clampedIndex !== currentIndex) {
            setCurrentIndex(clampedIndex);
          }
        }
      }, 150);
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, {passive: true});

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [enabled, totalItems, scrollThreshold, currentIndex]);

  // Enhanced setCurrentIndex that tracks manual navigation
  const enhancedSetCurrentIndex = (index: number) => {
    manualNavigationRef.current = true;
    setCurrentIndex(index);
    // Reset manual navigation flag after a delay
    setTimeout(() => {
      manualNavigationRef.current = false;
    }, 1000);
  };

  return {
    currentIndex,
    setCurrentIndex: enhancedSetCurrentIndex,
    carouselRef,
  };
};
